import React, { useEffect, useState } from "react";
import { CheckCircle2, Clock, Loader2, Ban, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const STATE = {
    done: { icon: CheckCircle2, tone: "text-emerald-300", label: (e) => `moved ${e.moved}` },
    pending: { icon: Clock, tone: "text-amber-200", label: () => "waiting for its next sync check" },
    unsupported: { icon: AlertTriangle, tone: "text-rose-300", label: () => "older build — re-tag there by hand" },
};

function EnvRow({ e, holding }) {
    const s = STATE[e.state] || STATE.pending;
    const Icon = s.icon;
    return (
        <li className="flex items-center justify-between rounded border border-white/10 px-3 py-1.5 text-xs" data-testid={`move-env-${e.env_id}`}>
            <span className="text-white/85">{e.name}{e.env_id === "self" && <span className="ml-1.5 text-[10px] text-white/40">(started here)</span>}</span>
            <span className={`inline-flex items-center gap-1.5 font-mono text-[11px] ${s.tone}`} data-testid={`move-env-state-${e.env_id}`}>
                <Icon className="w-3.5 h-3.5" /> {s.label(e)}{holding && e.state === "done" ? " · new documents" : ""}
            </span>
        </li>
    );
}

/** Live progress of a context move; polls while running and reports when the source context has been retired. */
export function ContextMoveProgress({ move: initial, onFinished }) {
    const [move, setMove] = useState(initial);
    const [busy, setBusy] = useState(false);
    const running = move.status === "running";

    useEffect(() => {
        if (!running) return undefined;
        const t = setInterval(async () => {
            try {
                const { data } = await api.get("/admin/datalake/contexts/moves", { params: { context_id: move.from } });
                const m = (data.items || []).find((x) => x.move_id === move.move_id);
                if (m) setMove(m);
                if (m && m.status !== "running") onFinished?.(m);
            } catch { /* next poll */ }
        }, 4000);
        return () => clearInterval(t);
    }, [running, move.from, move.move_id, onFinished]);

    const cancel = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/admin/datalake/contexts/moves/${move.move_id}/cancel`);
            setMove(data);
            onFinished?.(data);
            toast.success("Move stopped — the context stays active");
        } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
        setBusy(false);
    };

    const moved = (move.environments || []).reduce((n, e) => n + (e.moved || 0), 0);
    return (
        <div className="space-y-3" data-testid="move-progress">
            <div className={`rounded px-3 py-2 text-xs border ${move.status === "done" ? "border-emerald-400/30 bg-emerald-400/5 text-emerald-100" : move.status === "cancelled" ? "border-white/15 text-white/60" : "border-[#7C3AED]/30 bg-[#7C3AED]/[0.06] text-white/80"}`} data-testid="move-status">
                {move.status === "done" && <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> “{move.from}” retired — {moved} document{moved === 1 ? " now carries" : "s now carry"} “{move.to}”.</span>}
                {move.status === "cancelled" && <span className="inline-flex items-center gap-1.5"><Ban className="w-3.5 h-3.5" /> Move stopped{move.cancelled_by ? ` by ${move.cancelled_by}` : ""}; “{move.from}” stays active.</span>}
                {running && <span className="inline-flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Moving “{move.from}” → “{move.to}” · {moved} moved so far{move.waiting_for?.length ? ` · waiting for ${move.waiting_for.join(", ")}` : ""}</span>}
            </div>
            <ul className="space-y-1.5">{(move.environments || []).map((e) => <EnvRow key={e.env_id} e={e} holding={move.waiting_for?.includes(e.name)} />)}</ul>
            {running && (
                <div className="flex justify-end">
                    <button onClick={cancel} disabled={busy} className="text-[11px] text-white/50 hover:text-rose-200 transition-colors" data-testid="move-cancel">Stop this move</button>
                </div>
            )}
        </div>
    );
}
